import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';
import { offerForceRetry } from '../components/forceRetry';

/**
 * Signup detail — the screen you land on when a booking went wrong.
 *
 * Rebuilt on the second prototype ("Layout A"): a summary strip of activity /
 * user / state, then the event timeline, then the facts in four grouped cards,
 * with the actions as a list in the right column.
 *
 * **The timeline is built from real events only.** The prototype's version shows
 * rows we have no data for — the channel a booking came from ("app iOS"), the
 * provider endpoint, email subjects, the reason a user gave for cancelling,
 * refund progress. None of that is in `getSignupById`, and a plausible-looking
 * history is worse than a short one on the page people use to reconstruct what
 * happened. So each row here comes from a field that exists: the signup's
 * creation, each external booking attempt with its outcome, the confirmation
 * emails, the cancellation. A real event log would need a backend that records
 * one; until then this is what is true.
 *
 * Every `confirm()` and `prompt()` is gone, replaced by the design's dialog.
 * That is not cosmetic: a native prompt has room for one line, so the two
 * genuinely dangerous actions here used to be guarded by a sentence. Now they
 * carry the consequence and, for force-cancel, a warning strip.
 */

const fmt = (value, len = 16) => (value ? String(value).replace('T', ' ').slice(0, len) : '—');

// When the crawler last saw the signup's activity. Not buried: the latest crawl covering its
// date (last 24 h). Buried (no longer found, or taken by a booking): the last covering crawl that
// finished before it was buried - the one that did not find it is no sighting.
const cet = value => (value
  ? new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`)
      .toLocaleString('sv-SE', { timeZone: 'Europe/Copenhagen' }).slice(0, 16) + ' CET'
  : null);

const activityLastSeenLabel = ({ activityIsIntegrated, activityIsBuried, activityLastSeenUtc, activityBuriedAtUtc }) => {
  if (!activityIsIntegrated) return 'WannaSport inventory (not crawled)';
  const seen = cet(activityLastSeenUtc);
  if (activityIsBuried) {
    const gone = cet(activityBuriedAtUtc);
    if (!gone) return 'No longer listed (removed before removal times were recorded)';
    return seen
      ? `${seen} — no longer listed since ${gone}`
      : `Not seen in the 48 h before ${gone}, when it stopped being listed`;
  }
  return seen ? `${seen} (still listed)` : '> 24 h ago (no covering crawl in the last 24 h)';
};

const time = value => (value ? String(value).slice(11, 19) : '');
const day = value => (value ? String(value).slice(0, 10) : '');

const camelToWords = text => {
  const value = text == null ? '' : String(text);
  const spaced = value.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const yesNo = value => (value === true ? 'Yes' : value === false ? 'No' : '—');

/**
 * The events we can prove, in order. Actors match the prototype's legend, which
 * is what the dot colours key off.
 */
function buildTimeline(signup) {
  const events = [];

  if (signup.createdAtCet) {
    events.push({
      at: signup.createdAtCet,
      actor: 'wannasport',
      title: 'Signup created',
      desc: [
        signup.reference && `Reference ${signup.reference}`,
        signup.numberOfParticipants && `${signup.numberOfParticipants} participant${signup.numberOfParticipants === 1 ? '' : 's'}`
      ].filter(Boolean).join(' · '),
      ref: signup.purchaseId && { label: 'Purchase', value: signup.purchaseId, to: `/next/purchase/${signup.purchaseId}` }
    });
  }

  (signup.externalPurchaseAttempts || []).forEach((attempt, index) => {
    const failed = attempt.status === 'Failure';
    events.push({
      at: attempt.startedAtCet,
      actor: 'external',
      state: attempt.status,
      title: `External booking attempt ${index + 1}`,
      // The error text belongs to the signup, not to the attempt, so it is only
      // attached to a failure — and only to the last one, since that is the one
      // it describes.
      desc: [
        attempt.endedAtCet && `ended ${time(attempt.endedAtCet)}`,
        failed && signup.errorText ? signup.errorText : null
      ].filter(Boolean).join(' · '),
      ref: { label: 'Attempt', value: attempt.id, href: `/externalpurchaseattempt/${attempt.id}` },
      bad: failed
    });
  });

  if (signup.externalPurchase?.id && signup.bookResult === 'Success') {
    events.push({
      at: signup.activityDate ? null : null,
      actor: 'external',
      state: 'Success',
      title: 'Booking confirmed by the provider',
      desc: signup.externalReference ? `External reference ${signup.externalReference}` : '',
      ref: { label: 'Booking', value: signup.externalPurchase.id, to: `/next/external-purchase/${signup.externalPurchase.id}` },
      // No timestamp of its own on the wire; it is placed after the attempts.
      undated: true
    });
  }

  String(signup.confirmations || '')
    .split(',').map(v => v.trim()).filter(Boolean)
    .forEach(id => events.push({
      actor: 'email',
      title: 'Confirmation email',
      // The email's own timestamp and subject live behind `/email/{id}`; not
      // fetched here, so the row says what it is and links out.
      desc: 'sent to the user',
      ref: { label: 'Email', value: id, href: `/email/${id}` },
      undated: true
    }));

  if (signup.cancellationDateUTC) {
    events.push({
      at: signup.cancellationDateUTC,
      actor: 'wannasport',
      state: signup.cancellationResult,
      title: 'Cancellation',
      desc: signup.cancellationResult ? `result: ${camelToWords(signup.cancellationResult)}` : '',
      bad: signup.cancellationResult === 'Failure'
    });
  }

  const dated = events.filter(e => e.at && !e.undated)
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));
  const undated = events.filter(e => !e.at || e.undated);

  return [...dated, ...undated];
}

const SignupInternal = ({ api }) => {
  const { signupId } = useParams();
  const { ask, modalProps } = useModal();

  const [signup, setSignup] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      setSignup(await api.signups.getSignupById(signupId));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, signupId]);

  useEffect(() => {
    load();
  }, [load]);

  const say = (tone, text) => setNote({ tone, text });

  // Thrown on failure so `useModal` keeps the dialog's busy state honest and the
  // page reports it once.
  const run = async (fn, okText, failText) => {
    const result = await fn();
    const ok = result?.success !== false;
    say(ok ? 'info' : 'error', ok ? okText : (result?.message || failText));
    await load();
    if (!ok) throw new Error(failText);
  };

  const shortId = String(signupId).split('-')[0];
  const ctx = `signup …${String(signupId).slice(-6)}`;

  const retry = isBooking => {
    const what = isBooking ? 'booking' : 'cancellation';
    const already = (isBooking ? signup.bookResult : signup.cancellationResult) === 'Success';

    ask({
      kind: already ? 'danger' : 'confirm',
      title: already ? `Retry a ${what} that already succeeded?` : `Retry this ${what}?`,
      context: `Retry ${what} · ${ctx}`,
      body: already
        ? `This ${what} already came back Success. Sending it again asks the provider for the same slot a second time.`
        : `A fresh request goes to the external system with the same activity and court.`,
      warning: already
        ? 'A second successful booking is not undone by this admin — you would have to cancel one by hand at the venue.'
        : undefined,
      confirmLabel: already ? `Retry anyway` : `Retry ${what}`,
      onConfirm: () => run(
        async () => {
          const result = await api.overview.retry(signupId, isBooking, false);
          if (result?.canForce) {
            offerForceRetry(ask, {
              what,
              context: `Retry ${what} · ${ctx}`,
              message: result.message,
              onConfirm: () => run(
                () => api.overview.retry(signupId, isBooking, true),
                'Put back in the queue.',
                'Unable to put it back in the queue.'
              )
            });
          }
          return result;
        },
        'Put back in the queue.',
        'Unable to put it back in the queue.'
      )
    });
  };

  /**
   * Stop this signup appearing in the Overview queue.
   *
   * Two separate flags, one per direction, and the old detail page offers both.
   * They were carried over as read-only facts and the actions were not — the
   * rebuilt page grew eight actions to the old page's four and lost these two on
   * the way, which is a regression against the rule the rewrite set itself.
   *
   * It is used: production holds 1,179 muted bookings and 851 muted
   * cancellations.
   *
   * **One way.** There is exactly one mute function in `src/api` and no unmute
   * anywhere, so the dialog says so rather than letting somebody discover it
   * afterwards.
   *
   * The dialog describes the queue and stops there, on purpose. What muting does
   * beyond the overview — whether a retry job still picks the signup up, say —
   * is a backend question, and this was written in a round where the backend was
   * out of scope. Saying "the booking itself is unchanged" would have been a
   * guess dressed as a reassurance, in a dialog somebody reads before acting.
   */
  const mute = isBooking => {
    const what = isBooking ? 'booking' : 'cancellation';

    ask({
      kind: 'confirm',
      title: `Stop showing this ${what} in the overview?`,
      context: `Mute ${what} · ${ctx}`,
      body: `It stops appearing in the Overview queue, so nobody is prompted to act on it again. Use it for a ${what} somebody has already dealt with — clearing the reminder is not the same as completing it.`,
      warning: 'Nothing in this admin un-mutes a signup.',
      confirmLabel: `Mute ${what}`,
      onConfirm: () => run(
        () => api.overview.mute(signupId, isBooking),
        `${isBooking ? 'Booking' : 'Cancellation'} muted.`,
        `Unable to mute the ${what}.`
      )
    });
  };

  const sendConfirmation = isBooking => ask({
    kind: 'confirm',
    title: `Send the ${isBooking ? 'booking' : 'cancellation'} confirmation?`,
    context: `Confirm ${isBooking ? 'booking' : 'cancellation'} · ${ctx}`,
    body: 'The confirmation email is sent to the user at the address on the signup.',
    confirmLabel: 'Send email',
    onConfirm: () => run(
      () => api.overview.sendConfirmation(signupId, isBooking),
      'Confirmation sent.',
      'Unable to send the confirmation.'
    )
  });

  const addSecretCode = () => ask({
    kind: 'input',
    title: 'Add a secret code',
    context: `Add secret code · ${ctx}`,
    body: 'The code is given to the user for door access and replaces the one received from the provider.',
    label: 'Secret code',
    placeholder: 'e.g. 4821',
    hint: '4–8 alphanumeric characters',
    mono: true,
    confirmLabel: 'Save code',
    onConfirm: code => run(
      () => api.signups.addSecretCode(signupId, code.trim()),
      `Secret code ${code.trim()} added.`,
      'Unable to add the secret code.'
    )
  });

  const editComment = () => ask({
    kind: 'input',
    title: 'Internal comment',
    context: `Edit comment · ${ctx}`,
    body: 'Visible to administrators only, on the signup.',
    label: 'Comment',
    placeholder: 'e.g. user called, waiting for confirmation',
    multiline: true,
    initialValue: signup.signupComment || '',
    confirmLabel: 'Save comment',
    onConfirm: comment => run(
      () => api.overview.editComment(signupId, comment),
      'Comment changed.',
      'Unable to change the comment.'
    )
  });

  // Two dialogs, because they are two different decisions. The first asks
  // whether to cancel at all; only if the answer is "not externally" does the
  // second one spell out the divergence that creates.
  const forceCancellation = () => ask({
    kind: 'danger',
    title: 'Force the cancellation?',
    context: `Force cancellation · ${ctx}`,
    body: 'The signup is marked cancelled in WannaSport and a cancellation is sent to the external system.',
    warning: 'If the provider refuses it, the court may stay booked at the venue. Check it afterwards.',
    confirmLabel: 'Cancel signup',
    onConfirm: () => run(
      () => api.signups.forceCancellation(signupId, false),
      'Signup cancelled.',
      'Unable to cancel the signup.'
    )
  });

  const forceCancellationLocalOnly = () => ask({
    kind: 'danger',
    title: 'Cancel on our side only?',
    context: `Force cancellation, skip external · ${ctx}`,
    body: 'The signup is marked cancelled in WannaSport and nothing is sent to the external system.',
    warning: 'The booking stays live at the venue while our records say cancelled. Only do this if you have already cancelled it by hand.',
    confirmLabel: 'Cancel locally',
    onConfirm: () => run(
      () => api.signups.forceCancellation(signupId, true),
      'Signup cancelled locally.',
      'Unable to cancel the signup.'
    )
  });

  const moveTo = court => ask({
    kind: court.isAvailable ? 'confirm' : 'danger',
    title: `Move this signup to ${court.courtName}?`,
    context: `Change court · ${ctx}`,
    body: court.isAvailable
      ? 'The slot looks available on the external system, so the move should go through.'
      : 'The external system does not report this slot as available.',
    warning: court.isAvailable
      ? undefined
      : 'The move assumes you have already booked this slot by hand at the venue.',
    confirmLabel: 'Move signup',
    onConfirm: async () => {
      const { data } = await api.signups.changeCourt(signupId, court.id, false);

      if (data === 'Success') {
        say('info', 'Signup moved.');
        await load();
        return;
      }

      if (data === 'External purchase is already booked, invoke with force to override') {
        await load();
        // The one place this screen can create a double booking, so it gets its
        // own dialog rather than a second line in the first one.
        ask({
          kind: 'danger',
          title: 'The slot is already booked externally',
          context: `Force change court · ${ctx}`,
          body: `Forcing the move puts the signup on ${court.courtName} without releasing the existing external booking.`,
          warning: 'You may end up with two bookings at the venue. Neither is undone by this admin.',
          confirmLabel: 'Force the move',
          onConfirm: async () => {
            const forced = await api.signups.changeCourt(signupId, court.id, true);
            const ok = forced?.data === 'Success';
            say(ok ? 'info' : 'error', ok ? 'Signup moved (forced).' : `Unable to move: ${forced?.data}`);
            await load();
            if (!ok) throw new Error('move failed');
          }
        });
        return;
      }

      say('error', `Unable to move: ${data}`);
      await load();
      throw new Error('move failed');
    }
  });

  if (failed) {
    return (
      <div className="next-page">
        <div className="next-note next-note--error">
          Could not load this signup.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!signup) return <div className="next-page next-muted">Loading…</div>;

  const timeline = buildTimeline(signup);
  // `confirmations` is a comma-separated list of email ids, not one id.
  const confirmationIds = String(signup.confirmations || '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);
  const attempts = signup.externalPurchaseAttempts || [];
  const alternates = signup.alternateCourts || [];

  const groups = [
    {
      title: 'Booking',
      fields: [
        ['Facility name', signup.facilityName],
        ['Category', signup.category],
        ['Court name', signup.courtName],
        ['Activity date (CET)', fmt(signup.activityDate)],
        ['Last seen by crawler', activityLastSeenLabel(signup)],
        ['Duration', signup.duration && `${signup.duration} min`],
        ['Participants', signup.numberOfParticipants],
        ['Booking status', camelToWords(signup.status)],
        ['Created at (CET)', fmt(signup.createdAtCet, 19)]
      ]
    },
    {
      title: 'User & payment',
      fields: [
        ['User', [signup.userName, signup.userEmail, signup.userPhoneNumber].filter(Boolean).join(' · ')],
        ['User id', { guid: signup.userId, to: `/next/user/${signup.userId}` }],
        ['Purchase id', { guid: signup.purchaseId, to: `/next/purchase/${signup.purchaseId}` }],
        ['Part of a bundle', yesNo(signup.isBundle)],
        ['Reference', signup.reference]
      ]
    },
    {
      title: 'External system',
      fields: [
        ['External reference', signup.externalReference],
        ['External booking', signup.externalPurchase
          ? { guid: signup.externalPurchase.id, to: `/next/external-purchase/${signup.externalPurchase.id}` }
          : null],
        ['External activity', signup.externalPurchase
          ? { guid: signup.externalPurchase.externalActivityId, href: `/externalactivity/${signup.externalPurchase.externalActivityId}` }
          : null],
        ['Book result', signup.bookResult],
        ['Cancellation result', signup.cancellationResult],
        ['Error text', signup.errorText, signup.errorText ? 'bad' : null]
      ]
    },
    {
      title: 'Flags, access & ids',
      fields: [
        ['Has door opening', yesNo(signup.hasDoorOpening)],
        ...(signup.hasDoorOpening ? [
          ['Door opens (UTC)', fmt(signup.doorOpeningStartTimeUTC, 19)],
          ['Door closes (UTC)', fmt(signup.doorOpeningEndTimeUTC, 19)]
        ] : []),
        ['Secret code', signup.secretCode],
        ['Mute booking', yesNo(signup.muteBooking)],
        ['Mute cancellation', yesNo(signup.muteCancellation)],
        ['Activity id', { guid: signup.activityId, href: `/activity/${signup.activityId}` }],
        ['Facility id', { guid: signup.facilityId, href: `/facility/${signup.facilityId}` }],
        ['Cancelled at (UTC)', fmt(signup.cancellationDateUTC, 19)]
      ]
    }
  ];

  // `[label, onClick, tone, disabledReason]`. The old page greys the two mute
  // actions once their flag is set and says nothing about why; a disabled
  // control that does not explain itself is a control somebody clicks twice and
  // then reports as broken.
  const actions = [
    ['Retry booking', () => retry(true)],
    ['Confirm booking', () => sendConfirmation(true)],
    ['Mute booking', () => mute(true), null,
      signup.muteBooking ? 'Already muted — it does not appear in the overview.' : null],
    ['Retry cancellation', () => retry(false)],
    ['Confirm cancellation', () => sendConfirmation(false)],
    ['Mute cancellation', () => mute(false), null,
      signup.muteCancellation ? 'Already muted — it does not appear in the overview.' : null],
    ['Edit signup comment', editComment],
    ['Add secret code', addSecretCode],
    ['Force cancellation', forceCancellation, 'danger'],
    ['Force cancellation (skip external)', forceCancellationLocalOnly, 'danger']
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/signups">← All signups</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{[signup.category, signup.facilityName].filter(Boolean).join(' · ')}</h1>
          <StatusPill status={signup.status}>{camelToWords(signup.status)}</StatusPill>
        </div>
        <span className="next-detail__id">{signup.id}</span>
      </header>

      {signup.messageFromProviderAdmin && (
        <div className="next-note next-note--warn">
          <strong>From the provider admin:</strong> {signup.messageFromProviderAdmin}
        </div>
      )}

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          {/* Activity, user, state — what you need before deciding anything. */}
          <section className="next-card next-summary">
            <div className="next-summary__cell">
              <span className="next-summary__label">Activity</span>
              <span className="next-summary__big">{fmt(signup.activityDate)}</span>
              <span className="next-summary__sub">
                {[signup.courtName, signup.duration && `${signup.duration} min`, signup.category].filter(Boolean).join(' · ')}
              </span>
              <span className="next-summary__sub">{signup.facilityName}</span>
            </div>
            <div className="next-summary__cell">
              <span className="next-summary__label">User</span>
              <span className="next-summary__name">{signup.userName || '—'}</span>
              <span className="next-summary__sub">{signup.userEmail}</span>
              <span className="next-summary__sub">
                {[signup.userPhoneNumber, signup.numberOfParticipants && `${signup.numberOfParticipants} participant${signup.numberOfParticipants === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
              </span>
            </div>
            <div className="next-summary__cell">
              <span className="next-summary__label">State</span>
              <span className="next-summary__pills">
                <StatusPill status={signup.status}>{camelToWords(signup.status)}</StatusPill>
                {signup.cancellationResult && signup.cancellationResult !== 'Undefined' && (
                  <StatusPill status={signup.cancellationResult}>
                    Cancellation: {camelToWords(signup.cancellationResult)}
                  </StatusPill>
                )}
              </span>
              <span className="next-summary__mono">
                {signup.reference ? `Ref ${signup.reference}` : ''}
              </span>
              <span className="next-summary__mono">
                {signup.externalReference ? `Ext ${signup.externalReference}` : ''}
              </span>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Event timeline</h2>
              <span className="next-card__spacer" />
              <span className="next-legend">
                <span className="next-legend__item"><i className="next-dot next-dot--wannasport" />WannaSport</span>
                <span className="next-legend__item"><i className="next-dot next-dot--external" />External system</span>
                <span className="next-legend__item"><i className="next-dot next-dot--email" />Email</span>
              </span>
            </div>

            {timeline.length === 0 ? (
              <p className="next-table__note next-muted">Nothing recorded for this signup.</p>
            ) : (
              <div className="next-timeline">
                {timeline.map((event, index) => {
                  const prev = timeline[index - 1];
                  const newDay = event.at && day(event.at) !== day(prev?.at);

                  return (
                    <React.Fragment key={`${event.title}-${index}`}>
                      {newDay && <div className="next-timeline__day">{day(event.at)}</div>}
                      <div className="next-timeline__row">
                        <span className="next-timeline__time">{event.at ? time(event.at) : '—'}</span>
                        <span className={`next-dot next-dot--${event.actor}`} aria-hidden />
                        <span className="next-timeline__body">
                          <span className="next-timeline__title">
                            <span className={event.bad ? 'next-timeline__bad' : undefined}>{event.title}</span>
                            {event.state && event.state !== 'Undefined' && (
                              <StatusPill status={event.state}>{camelToWords(event.state)}</StatusPill>
                            )}
                          </span>
                          {event.desc && <span className="next-timeline__desc">{event.desc}</span>}
                          {event.ref && (
                            <span className="next-timeline__ref">
                              {event.ref.label} <Guid value={event.ref.value} to={event.ref.to} href={event.ref.href} />
                            </span>
                          )}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </section>

          <div className="next-groups">
            {groups.map(({ title, fields }) => (
              <section className="next-card" key={title}>
                <div className="next-card__head"><h2>{title}</h2></div>
                <div className="next-facts next-facts--single">
                  {fields.filter(Boolean).map(([label, value, tone]) => (
                    <div className="next-fact" key={label}>
                      <span className="next-fact__label">{label}</span>
                      {value && typeof value === 'object' && 'guid' in value ? (
                        // `to` for a page this tree has rebuilt, `href` for one
                        // that still lives in the old admin.
                        <Guid value={value.guid} to={value.to} href={value.href} />
                      ) : (
                        <span className={`next-fact__value${tone === 'bad' ? ' next-fact__value--bad' : ''}`}>
                          {value === '' || value == null ? '—' : value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <section className="next-card">
            <div className="next-card__head"><h2>External booking attempts</h2></div>
            {attempts.length === 0 ? (
              <p className="next-table__note next-muted">No attempt recorded.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Started (CET)</th>
                      <th>Ended (CET)</th>
                      <th className="next-table__right">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map(attempt => (
                      <tr key={attempt.id}>
                        <td data-label="Status"><StatusPill status={attempt.status}>{camelToWords(attempt.status)}</StatusPill></td>
                        <td data-label="Started (CET)" className="next-nowrap">{fmt(attempt.startedAtCet, 19)}</td>
                        <td data-label="Ended (CET)" className="next-nowrap">{fmt(attempt.endedAtCet, 19)}</td>
                        <td className="next-table__right" data-label="">
                          <a className="next-link" to={`/next/external-purchase-attempt/${attempt.id}`}>Details</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {alternates.length > 0 && (
            <section className="next-card">
              <div className="next-card__head">
                <h2>Equivalent slots</h2>
                <span className="next-muted">
                  {alternates.length} alternative{alternates.length === 1 ? '' : 's'} to move this signup to
                </span>
              </div>
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Slot id</th>
                      <th>Court</th>
                      <th>Available externally</th>
                      <th className="next-table__right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alternates.map(court => (
                      <tr key={court.id}>
                        <td data-label=""><Guid value={court.id} to={`/next/activity/${court.id}`} /></td>
                        <td data-label="Court">{court.courtName}</td>
                        <td data-label="Available externally">
                          <StatusPill status={court.isAvailable ? 'Success' : 'Failure'}>
                            {court.isAvailable ? 'Yes' : 'No'}
                          </StatusPill>
                        </td>
                        <td data-label="Details" className="next-table__right">
                          <button type="button" className="next-btn next-btn--sm" onClick={() => moveTo(court)}>
                            Move here
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            {/* Text links, as the prototype has them: a column of seven buttons
                reads as seven equal risks, which these are not. */}
            <div className="next-actionlist">
              {actions.map(([label, onClick, tone, disabledReason]) => (
                <button
                  type="button"
                  key={label}
                  className={`next-actionlist__item${tone === 'danger' ? ' next-actionlist__item--danger' : ''}`}
                  disabled={!!disabledReason}
                  title={disabledReason || undefined}
                  onClick={onClick}
                >
                  {label}
                  {disabledReason && <span className="next-actionlist__why">muted</span>}
                </button>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                signup.userId && {
                  label: 'User',
                  hint: signup.userName || signup.userEmail,
                  to: `/next/user/${signup.userId}`
                },
                signup.purchaseId && {
                  label: 'Purchase',
                  hint: signup.reference ? `Ref ${signup.reference}` : undefined,
                  to: `/next/purchase/${signup.purchaseId}`
                },
                // Each confirmation email by id, then the search that finds any
                // that arrive later.
                ...confirmationIds.map((id, index) => ({
                  label: confirmationIds.length > 1 ? `Email ${index + 1}` : 'Email',
                  hint: String(id).split('-')[0],
                  to: `/next/email/${id}`
                })),
                { label: 'All emails for this signup', to: `/next/emails?signupId=${signupId}` },
                signup.facilityId && {
                  label: 'Signups at this facility',
                  hint: signup.facilityName,
                  to: `/next/signups?facilityId=${signup.facilityId}`
                },
                signup.userId && {
                  label: 'This user’s signups',
                  to: `/next/signups?user=${signup.userId}`
                },
                signup.purchaseId && {
                  label: 'Participants',
                  hint: 'on this purchase',
                  to: `/next/participants?purchaseId=${signup.purchaseId}`
                },
                signup.externalPurchase?.id && {
                  label: 'External booking',
                  to: `/next/external-purchase/${signup.externalPurchase.id}`
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

SignupInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Signup = withApi(SignupInternal);

Signup.requiresLogin = true;

export default Signup;
